/**
 * Connection Manager
 * Handles backend connection failures and implements circuit breaker pattern
 */

/**
 * Connection status interface
 */
export interface ConnectionStatus {
  failureCount: number;
  maxFailures: number;
  isCircuitOpen: boolean;
  lastFailureTime: number | null;
  canMakeRequest: boolean;
}

/**
 * Status change listener callback type
 */
export type StatusChangeListener = (status: ConnectionStatus) => void;

/**
 * Connection Manager Class
 * Implements circuit breaker pattern to prevent cascading failures
 */
class ConnectionManager {
  private failureCount: number = 0;
  private maxFailures: number = 5; // Stop after 5 consecutive failures
  private isCircuitOpen: boolean = false;
  private lastFailureTime: number | null = null;
  private resetTimeout: number = 60000; // Reset after 1 minute
  private listeners: Set<StatusChangeListener> = new Set();

  /**
   * Record a successful request
   * Resets failure count and opens circuit if it was closed
   */
  recordSuccess(): void {
    if (this.failureCount > 0) {
      console.log("Connection restored after", this.failureCount, "failures");
    }
    this.failureCount = 0;
    this.isCircuitOpen = false;
    this.lastFailureTime = null;
    this.notifyListeners();
  }

  /**
   * Record a failed request
   * Increments failure count and opens circuit if threshold is reached
   *
   * @param error - The error that occurred
   */
  recordFailure(error: Error | any): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();

    const message = error?.message || String(error);
    console.error(`API Request failed (${this.failureCount}/${this.maxFailures}):`, message);

    if (this.failureCount >= this.maxFailures && !this.isCircuitOpen) {
      this.openCircuit();
    }

    this.notifyListeners();
  }

  /**
   * Open the circuit breaker - stop making requests
   * Schedules automatic reset after timeout period
   */
  private openCircuit(): void {
    this.isCircuitOpen = true;
    console.error("🔴 Circuit breaker opened - Backend appears to be unavailable");
    console.error("Possible causes:");
    console.error("  - Backend server is not running or not accessible");
    console.error("  - Network connectivity issues");
    console.error("  - Backend is starting up");
    console.error("The application will stop making requests to prevent overload.");

    // Schedule automatic reset
    setTimeout(() => {
      this.tryReset();
    }, this.resetTimeout);
  }

  /**
   * Try to reset the circuit breaker
   * Performs a partial reset to test if backend is available
   */
  private tryReset(): void {
    console.log("Attempting to reset circuit breaker...");
    this.isCircuitOpen = false;
    this.failureCount = Math.floor(this.maxFailures / 2); // Partial reset
    this.notifyListeners();
  }

  /**
   * Manually reset the connection manager
   * Fully resets all counters and reopens circuit
   */
  reset(): void {
    console.log("Manually resetting connection manager...");
    this.failureCount = 0;
    this.isCircuitOpen = false;
    this.lastFailureTime = null;
    this.notifyListeners();
  }

  /**
   * Check if requests should be allowed
   *
   * @returns True if requests can be made, false otherwise
   */
  canMakeRequest(): boolean {
    // Allow request if circuit is closed
    if (!this.isCircuitOpen) {
      return true;
    }

    // Check if enough time has passed for auto-reset attempt
    if (this.lastFailureTime && Date.now() - this.lastFailureTime > this.resetTimeout) {
      this.tryReset();
      return true;
    }

    return false;
  }

  /**
   * Get current connection status
   *
   * @returns Current connection status
   */
  getStatus(): ConnectionStatus {
    return {
      failureCount: this.failureCount,
      maxFailures: this.maxFailures,
      isCircuitOpen: this.isCircuitOpen,
      lastFailureTime: this.lastFailureTime,
      canMakeRequest: this.canMakeRequest(),
    };
  }

  /**
   * Add a listener for status changes
   *
   * @param callback - Function to call when status changes
   * @returns Function to remove the listener
   */
  addListener(callback: StatusChangeListener): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of status change
   */
  private notifyListeners(): void {
    const status = this.getStatus();
    this.listeners.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        console.error("Error in connection manager listener:", error);
      }
    });
  }
}

// Export singleton instance
export const connectionManager = new ConnectionManager();

// Expose for debugging
if (typeof window !== "undefined") {
  (window as any).connectionManager = connectionManager;
}
